// Hold to talk. Tap to type, hold to talk; release sends; slide up cancels. Records 16 kHz mono WAV (audio/recorder.ts).
//
//   useHoldToTalk({ onAudio, onTap, tap })  -> { phase, levels, ms, bind, cancel, stop }   gesture + recorder, any look
//   <HoldToTalk onTap={openKeyboard} onAudio={send} left={<plus/>} right={<camera/>} />   chat input bar (52px)
//     After release the bar is back at once (dimmed while the clip is encoded, and while `disabled`): the chat shows
//     the message on its way as a voice bubble (feedback 11-03), so the bar itself never says "Sending…".
//   <TalkButton onAudio={send} />                                                         O8 round 76px button
//   <DeskTalkBar value={draft} onInput={setDraft} onSend={send} onAudio={sendVoice} />   desktop chat bar: text field + mic
//
// tap: 'callback' (default) calls onTap on a short tap; 'toggle' starts recording on a tap and sends on the next tap
// (also what Enter/Space do), for people who can't hold. Recordings shorter than minMs are dropped (onTooShort).
import type { ComponentChildren, JSX, Ref } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Recorder, RecorderError, primeAudio, MAX_MS, type Clip } from '../audio/recorder';
import { haptic } from '../native';
import { Icon } from './icons';
import s from './HoldToTalk.module.css';

export type TalkPhase = 'idle' | 'pressing' | 'starting' | 'recording' | 'cancel' | 'sending';

export interface HoldToTalkOptions {
  onAudio: (clip: Clip) => void | Promise<unknown>;
  onTap?: () => void;
  tap?: 'callback' | 'toggle';
  holdMs?: number;
  minMs?: number;
  maxMs?: number;
  /** Upward distance (px) that arms cancel. Default 60. */
  cancelDistance?: number;
  onError?: (e: RecorderError) => void;
  onTooShort?: () => void;
  disabled?: boolean;
}

const BARS = 24;
const EMPTY = Array.from({ length: BARS }, () => 0);

export function useHoldToTalk(o: HoldToTalkOptions) {
  const opts = useRef(o);
  opts.current = o;
  const [phase, setPhaseState] = useState<TalkPhase>('idle');
  const [levels, setLevels] = useState<number[]>(EMPTY);
  const [ms, setMs] = useState(0);
  const [toggled, setToggled] = useState(false);
  const phaseRef = useRef<TalkPhase>('idle');
  const g = useRef({ x: 0, y: 0, id: -1, timer: 0 as unknown as ReturnType<typeof setTimeout> | 0, toggled: false, stopOnUp: false, released: false, cancelZone: false, tick: 0 as unknown as ReturnType<typeof setInterval> | 0, lastLevel: 0 });
  const rec = useRef<Recorder | null>(null);
  const setPhase = (p: TalkPhase) => { phaseRef.current = p; setPhaseState(p); };

  const clearTimers = () => {
    if (g.current.timer) { clearTimeout(g.current.timer); g.current.timer = 0; }
    if (g.current.tick) { clearInterval(g.current.tick); g.current.tick = 0; }
  };

  const finish = async (send: boolean) => {
    const r = rec.current;
    rec.current = null;
    clearTimers();
    g.current.toggled = false; setToggled(false);
    g.current.cancelZone = false;
    if (!r) { setPhase('idle'); return; }
    const elapsed = r.elapsed;
    if (!send) { r.cancel(); setPhase('idle'); setLevels(EMPTY); setMs(0); return; }
    if (elapsed < (opts.current.minMs ?? 500)) { r.cancel(); setPhase('idle'); setLevels(EMPTY); setMs(0); opts.current.onTooShort?.(); return; }
    setPhase('sending');
    haptic('light');
    try {
      const clip = await r.stop();
      await opts.current.onAudio(clip);
    } catch (e) {
      if (e instanceof RecorderError) opts.current.onError?.(e);
      else console.warn(e);
    } finally {
      setPhase('idle'); setLevels(EMPTY); setMs(0);
    }
  };

  const begin = async (toggle: boolean) => {
    g.current.toggled = toggle; setToggled(toggle);
    g.current.released = false;
    setPhase('starting');
    const r = new Recorder();
    rec.current = r;
    r.onLevel = (lv) => {
      const now = Date.now();
      if (now - g.current.lastLevel < 80) return;
      g.current.lastLevel = now;
      setLevels((prev) => prev.slice(1).concat([lv]));
    };
    try {
      await r.start();
    } catch (e) {
      rec.current = null; g.current.toggled = false; setToggled(false); setPhase('idle');
      opts.current.onError?.(e instanceof RecorderError ? e : new RecorderError('failed', 'Could not start the microphone.'));
      return;
    }
    if (rec.current !== r) { r.cancel(); return; } // cancelled while starting
    if (!toggle && g.current.released) { await finish(false); return; } // let go while the mic was starting
    haptic('medium');
    setPhase(g.current.cancelZone ? 'cancel' : 'recording');
    g.current.tick = setInterval(() => {
      const el = r.elapsed;
      setMs(el);
      if (el >= (opts.current.maxMs ?? MAX_MS)) void finish(true);
    }, 200);
  };

  const cancel = () => { clearTimers(); if (rec.current) void finish(false); else setPhase('idle'); };
  /** Send what was recorded so far (a Send button, or Enter anywhere while recording). */
  const stop = () => { if (rec.current && phaseRef.current === 'recording') void finish(true); };

  useEffect(() => () => { clearTimers(); rec.current?.cancel(); rec.current = null; }, []);

  const active = () => phaseRef.current === 'starting' || phaseRef.current === 'recording' || phaseRef.current === 'cancel';

  const bind = {
    onPointerDown: (e: PointerEvent) => {
      if (opts.current.disabled || (e.pointerType === 'mouse' && e.button !== 0)) return;
      primeAudio(); // must run inside the gesture (iOS)
      if (g.current.toggled && active()) { g.current.stopOnUp = true; return; }
      if (phaseRef.current !== 'idle') return;
      const el = e.currentTarget as HTMLElement;
      try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      g.current.x = e.clientX; g.current.y = e.clientY; g.current.id = e.pointerId; g.current.cancelZone = false; g.current.released = false;
      setPhase('pressing');
      g.current.timer = setTimeout(() => { g.current.timer = 0; void begin(false); }, opts.current.holdMs ?? 300);
    },
    onPointerMove: (e: PointerEvent) => {
      if (e.pointerId !== g.current.id) return;
      const p = phaseRef.current;
      if (p === 'pressing') {
        if (Math.hypot(e.clientX - g.current.x, e.clientY - g.current.y) > 14) { clearTimers(); setPhase('idle'); }
        return;
      }
      if (!g.current.toggled && (p === 'recording' || p === 'cancel' || p === 'starting')) {
        const c = g.current.y - e.clientY > (opts.current.cancelDistance ?? 60);
        if (c !== g.current.cancelZone) {
          g.current.cancelZone = c;
          if (p !== 'starting') { setPhase(c ? 'cancel' : 'recording'); haptic('selection'); }
        }
      }
    },
    onPointerUp: (e: PointerEvent) => {
      if (g.current.stopOnUp) { g.current.stopOnUp = false; void finish(true); return; }
      if (e.pointerId !== g.current.id) return;
      g.current.id = -1;
      if (g.current.timer) {
        clearTimers();
        setPhase('idle');
        if ((opts.current.tap ?? 'callback') === 'toggle') void begin(true);
        else opts.current.onTap?.();
        return;
      }
      if (g.current.toggled) return;
      const p = phaseRef.current;
      if (p === 'starting') { g.current.released = true; return; }
      if (p === 'recording' || p === 'cancel') void finish(!g.current.cancelZone);
    },
    onPointerCancel: (e: PointerEvent) => {
      if (e.pointerId !== g.current.id) return;
      g.current.id = -1;
      if (g.current.timer) { clearTimers(); setPhase('idle'); return; }
      if (!g.current.toggled) { if (phaseRef.current === 'starting') g.current.released = true; else if (active()) void finish(false); }
    },
    onContextMenu: (e: Event) => e.preventDefault(),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Escape' && active()) { e.preventDefault(); cancel(); return; }
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      if (e.repeat || opts.current.disabled) return;
      primeAudio();
      if (active()) { void finish(true); return; }
      if (phaseRef.current !== 'idle') return;
      if ((opts.current.tap ?? 'callback') === 'toggle') void begin(true);
      else opts.current.onTap?.();
    },
  };

  return { phase, levels, ms, toggled, bind, cancel, stop, recording: phase === 'recording' || phase === 'cancel' };
}

export const fmtMs = (ms: number) => { const s = Math.floor(ms / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

/** Live input level bars. */
export function Waveform({ levels, color = 'currentColor', bars = 24, height = 24 }: { levels: number[]; color?: string; bars?: number; height?: number }) {
  const lv = levels.slice(-bars);
  return (
    <span aria-hidden="true" style={{ display: 'flex', alignItems: 'center', gap: 3, height }}>
      {lv.map((v, i) => (
        <span key={i} style={{ width: 3, borderRadius: 2, background: color, height: Math.max(3, Math.round(height * Math.min(1, 0.12 + v))), transition: 'height 80ms linear' }} />
      ))}
    </span>
  );
}

// ------------------------------------------------------------------ chat input bar
export interface HoldToTalkProps extends Omit<HoldToTalkOptions, 'tap'> {
  /** Placeholder text (default "Tap to type, hold to talk"). */
  label?: string;
  /** Accessible name (default "Tap to type, or hold to talk"). Include the buddy: "...to talk to Margo". */
  ariaLabel?: string;
  /** Left slot (e.g. the [+] button) and right slot (camera). Hidden while recording. */
  left?: ComponentChildren;
  right?: ComponentChildren;
  style?: JSX.CSSProperties;
}

export function HoldToTalk({ label = 'Tap to type, hold to talk', ariaLabel, left, right, style, ...o }: HoldToTalkProps) {
  const t = useHoldToTalk({ ...o, tap: 'callback' });
  const rec = t.phase === 'recording' || t.phase === 'cancel' || t.phase === 'starting';
  const off = !!o.disabled || t.phase === 'sending';
  return (
    <div class={s.bar} style={style} data-phase={t.phase} data-disabled={off ? 'true' : 'false'}>
      {rec && <p class={s.hint} aria-hidden="true">{t.phase === 'cancel' ? 'Release to cancel' : 'Slide up to cancel'}</p>}
      <div class={s.slot} style={{ visibility: rec ? 'hidden' : 'visible' }}>{left}</div>
      <button type="button" class={s.center} aria-label={ariaLabel || 'Tap to type, or hold to talk'} {...t.bind} disabled={o.disabled}>
        {label}
      </button>
      <div class={s.slot} style={{ visibility: rec ? 'hidden' : 'visible' }}>{right}</div>
      {rec && (
        <div class={s.live} data-cancel={t.phase === 'cancel' ? 'true' : 'false'} role="status" aria-live="polite">
          {t.phase === 'cancel'
            ? <><Icon name="close" size={18} /><span>Release to cancel</span></>
            : <><Waveform levels={t.levels} bars={12} /><span>Listening… let go to send</span><span class={s.timer}>{fmtMs(t.ms)}</span></>}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ desktop chat bar
export interface DeskTalkBarProps {
  /** Buddy name: placeholder "Message {name}", tooltip "Click to talk to {name}". */
  name: string;
  value: string;
  onInput: (v: string) => void;
  /** Enter or the send button with text in the field. */
  onSend: () => void;
  onAudio: (clip: Clip) => void | Promise<unknown>;
  /** The [+] button (photo / file). The line look has none unless this is set. */
  onAttach?: () => void;
  onError?: (e: RecorderError) => void;
  onTooShort?: () => void;
  /** Esc or the cancel button while recording: nothing was sent. */
  onCancelled?: () => void;
  /** Waiting for the buddy: dimmed, nothing can be sent (the field keeps focus). */
  disabled?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  /** 'pill' (K1 chat, default) or 'line': the desktop room's frameless input under her subtitles (D_Desk): a thin line,
   *  "Say something to {name}…", and while recording a red dot, "Listening…", the waveform, the timer, X and send. */
  look?: 'pill' | 'line';
  /** Placeholder (default "Message {name}"). */
  placeholder?: string;
}

/** Desktop chat bar (feedback 11-03, decided on the canvas: fb1103 row "8 · 电脑端发语音"). Holding the mouse button to
 *  talk is tiring, and a global "hold ⌥ Space" needs a key-up that Electron shortcuts don't have (only a keyboard hook
 *  with macOS Input Monitoring), so on a computer: a text field (Enter sends) and a mic. Click the mic to talk, click
 *  it again (now a send arrow) or press Enter to send, Esc or the X cancels; holding the mic and letting go also
 *  works. With text in the field the mic becomes the send button. ⌥ Space (desktop shell) only brings the window
 *  forward with the cursor in the field. What was sent then shows like on the phone (VoiceBubble). */
export function DeskTalkBar({ name, value, onInput, onSend, onAudio, onAttach, onError, onTooShort, onCancelled, disabled, inputRef, look = 'pill', placeholder }: DeskTalkBarProps) {
  const t = useHoldToTalk({ onAudio, onError, onTooShort, disabled, tap: 'toggle' });
  const live = t.phase === 'starting' || t.phase === 'recording' || t.phase === 'cancel';
  const off = !!disabled || t.phase === 'sending';
  const typed = value.trim() !== '';
  const cancel = () => { t.cancel(); onCancelled?.(); };
  // While recording, Enter sends and Esc cancels wherever the focus is (the mic handles its own keys first).
  const keys = useRef({ stop: t.stop, cancel });
  keys.current = { stop: t.stop, cancel };
  useEffect(() => {
    if (!live) return undefined;
    const on = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat) return;
      if (e.key === 'Enter') { e.preventDefault(); keys.current.stop(); }
      else if (e.key === 'Escape') { e.preventDefault(); keys.current.cancel(); }
    };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, [live]);
  const hold = live && !t.toggled;
  const field = (cls: string) => (
    <input
      key="field" ref={inputRef} type="text" class={cls} aria-label={`Message ${name}`} placeholder={placeholder || `Message ${name}`} value={value}
      readOnly={off} autoComplete="off" enterKeyHint="send"
      onInput={(e) => onInput((e.currentTarget as HTMLInputElement).value)}
      onKeyDown={(e) => { if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); if (!off && typed) onSend(); } }}
    />
  );
  const mic = (cls: string, size: number) => (
    <button
      key="mic" type="button" class={cls} aria-label={live ? 'Send voice message' : `Talk to ${name}`}
      {...t.bind} disabled={off}
      onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Escape' && live) { e.preventDefault(); cancel(); } else t.bind.onKeyDown(e); }}
    >
      <Icon name={live ? 'send' : 'mic'} size={live ? size - 2 : size} strokeWidth={live ? 2.6 : 2} />
    </button>
  );
  if (look === 'line') {
    return (
      <div class={s.line} data-phase={t.phase} data-live={live ? 'true' : 'false'} data-disabled={off ? 'true' : 'false'}>
        {live && <p key="hint" class={s.lineHint} aria-hidden="true">{t.phase === 'cancel' ? 'Release to cancel' : hold ? 'Esc to cancel' : 'Enter to send · Esc to cancel'}</p>}
        {!live && onAttach && <button key="plus" type="button" class={s.lineBtn} aria-label="Add a photo or file" disabled={off} onClick={onAttach}><Icon name="plus" size={19} /></button>}
        {live
          ? (
            <div key="live" class={s.lineLive} role="status" aria-live="polite">
              <span class={s.lineDot} aria-hidden="true" />
              <span class={s.lineLabel}>{t.phase === 'cancel' ? 'Release to cancel' : hold ? 'Let go to send' : 'Listening…'}</span>
              <span class={s.lineWave}><Waveform levels={t.levels} bars={18} height={22} /></span>
              <span class={s.lineTimer}>{fmtMs(t.ms)}</span>
            </div>
          )
          : field(s.lineInput)}
        {live && <button key="x" type="button" class={s.lineX} aria-label="Cancel, don't send" onClick={cancel}><Icon name="close" size={15} strokeWidth={2.4} /></button>}
        {typed && !live
          ? <button key="send" type="button" class={`${s.lineMic} ${s.lineSend}`} aria-label="Send" disabled={off} onClick={onSend}><Icon name="send" size={17} strokeWidth={2.6} /></button>
          : mic(`${s.deskMic} ${s.lineMic}`, 19)}
        {!typed && !live && <span key="tip" class={s.tip} aria-hidden="true">{`Click to talk to ${name}`}</span>}
      </div>
    );
  }
  return (
    <div class={s.desk} data-phase={t.phase} data-live={live ? 'true' : 'false'} data-disabled={off ? 'true' : 'false'}>
      {live && <p class={s.deskHint} aria-hidden="true">{t.phase === 'cancel' ? 'Release to cancel' : hold ? 'Esc to cancel' : 'Enter to send · Esc to cancel'}</p>}
      {live
        ? <button type="button" class={s.deskBtn} aria-label="Cancel, don't send" onClick={cancel}><Icon name="close" size={18} strokeWidth={2.4} /></button>
        : onAttach && <button type="button" class={s.deskBtn} aria-label="Add a photo or file" disabled={off} onClick={onAttach}><Icon name="plus" size={21} /></button>}
      {live
        ? (
          <div class={s.deskLive} role="status" aria-live="polite">
            {t.phase === 'cancel'
              ? <span>Release to cancel</span>
              : <><Waveform levels={t.levels} bars={8} /><span>{hold ? 'Let go to send' : 'Listening…'}</span><span class={s.timer}>{fmtMs(t.ms)}</span></>}
          </div>
        )
        : (
          <input
            ref={inputRef} type="text" class={s.deskInput} aria-label={`Message ${name}`} placeholder={`Message ${name}`} value={value}
            readOnly={off} autoComplete="off" enterKeyHint="send"
            onInput={(e) => onInput((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); if (!off && typed) onSend(); } }}
          />
        )}
      {typed && !live
        ? <button type="button" class={`${s.deskBtn} ${s.deskSend}`} aria-label="Send" disabled={off} onClick={onSend}><Icon name="send" size={19} strokeWidth={2.4} /></button>
        : (
          <button
            type="button" class={`${s.deskBtn} ${s.deskMic}`} aria-label={live ? 'Send voice message' : `Talk to ${name}`}
            {...t.bind} disabled={off}
            onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Escape' && live) { e.preventDefault(); cancel(); } else t.bind.onKeyDown(e); }}
          >
            <Icon name={live ? 'send' : 'mic'} size={live ? 19 : 20} strokeWidth={live ? 2.4 : 2} />
          </button>
        )}
      {!typed && !live && <span class={s.tip} aria-hidden="true">{`Click to talk to ${name}`}</span>}
    </div>
  );
}

// ------------------------------------------------------------------ O8 round button
export interface TalkButtonProps extends Omit<HoldToTalkOptions, 'tap' | 'onTap'> {
  /** Buddy name for the accessible label ("Hold to talk to Margo"). */
  name: string;
  /** Text under the button while idle (default "Hold to talk"). */
  idleLabel?: string;
}

export function TalkButton({ name, idleLabel = 'Hold to talk', ...o }: TalkButtonProps) {
  const t = useHoldToTalk({ ...o, tap: 'toggle' });
  const live = t.phase === 'recording' || t.phase === 'cancel';
  const level = t.levels[t.levels.length - 1] || 0;
  const text = t.phase === 'starting' ? 'Listening…'
    : t.phase === 'recording' ? (t.toggled ? 'Listening… tap to send' : 'Listening… let go to send')
    : t.phase === 'cancel' ? 'Release to cancel'
    : t.phase === 'sending' ? 'One moment…'
    : idleLabel;
  return (
    <div class={s.round}>
      <button
        type="button" class={s.mic} aria-label={live ? 'Stop and send' : `Hold to talk to ${name}`} aria-pressed={live ? 'true' : 'false'}
        data-live={live || t.phase === 'starting' ? 'true' : 'false'} {...t.bind} disabled={o.disabled}
        style={{ boxShadow: live ? `0 0 0 ${8 + Math.round(level * 16)}px var(--acc-glow)` : '0 0 0 8px rgba(255,255,255,0.04)' }}
      >
        <Icon name="mic" size={30} />
      </button>
      <span class={s.roundLabel} role="status" aria-live="polite">{text}{live && <span class={s.timer}> {fmtMs(t.ms)}</span>}</span>
    </div>
  );
}
